import { yupResolver } from "@hookform/resolvers/yup";
import { If } from "components";
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormField, Input, Select, Switch } from "ui";

import { useDeliveryLocations, useDeliveryZoneExisting } from "../../../apis";
import { deliveryFeeSchema } from "../../../helpers";
import { AllowedResellersField } from "./atoms";
import {
  DeliveryFeeFormProps,
  DeliveryFeeFormValues
} from "./DeliveryFeeForm.types";

import styles from "./DeliveryFeeForm.module.scss";

const DeliveryFeeForm = ({
  formId,
  defaultValues,
  initialAllowedResellers,
  onSubmit,
  setIsSubmitButtonDisabled
}: DeliveryFeeFormProps) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue
  } = useForm<DeliveryFeeFormValues>({
    defaultValues,
    resolver: yupResolver(deliveryFeeSchema) as Resolver<any, any>
  });

  const selectedCountry = watch("country");
  const selectedCity = watch("city");
  const selectedNeighborhood = watch("neighborhood");
  const isHiddenFromResellers = watch("isHiddenFromResellers");

  const isAbidjan = (selectedCity ?? "").trim().toLowerCase() === "abidjan";

  useEffect(() => {
    if (defaultValues.id) return;
    if (!selectedCity) return;

    setValue("isExpedition", !isAbidjan);
  }, [selectedCity, isAbidjan, defaultValues.id]);

  const { isZoneExisting } = useDeliveryZoneExisting({
    country: selectedCountry,
    city: selectedCity,
    neighborhood: selectedNeighborhood
  });

  const { deliveryLocations } = useDeliveryLocations();

  const countryOptions = useMemo(() => {
    return deliveryLocations?.countries.map((country) => ({
      label: country.name,
      value: country.name
    }));
  }, [deliveryLocations]);

  const cityOptions = useMemo(() => {
    return deliveryLocations?.countries
      .find((country) => country.name === selectedCountry)
      ?.cities.map((city) => ({
        label: city.name,
        value: city.name
      }));
  }, [deliveryLocations, selectedCountry]);

  const areaOptions = useMemo(() => {
    return deliveryLocations?.countries
      .find((country) => country.name === selectedCountry)
      ?.cities.find((city) => city.name === selectedCity)
      ?.areas.map((area) => ({
        label: area.name,
        value: area.name
      }));
  }, [deliveryLocations, selectedCountry, selectedCity]);

  const isZoneExistingError =
    (defaultValues.country !== selectedCountry ||
      defaultValues.city !== selectedCity ||
      defaultValues.neighborhood !== selectedNeighborhood) &&
    isZoneExisting;

  useEffect(() => {
    setIsSubmitButtonDisabled(isZoneExistingError);
  }, [isZoneExistingError]);

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="country"
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("Country")}
            value={value}
            onChange={(selectedValue) => {
              onChange(selectedValue);
              setValue("city", "");
              setValue("neighborhood", "");
            }}
            placeholder={t("Choose Country")}
            errorMessage={errors.country?.message}
            options={countryOptions ?? []}
          />
        )}
      />

      <Controller
        control={control}
        name="city"
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("City")}
            value={value}
            onChange={(selectedValue) => {
              onChange(selectedValue);
              setValue("neighborhood", "");
            }}
            placeholder={t("Choose City")}
            errorMessage={errors.city?.message}
            options={cityOptions ?? []}
          />
        )}
      />

      <FormField
        errorMessage={
          isZoneExistingError ? t("Delivery zone already exists") : null
        }
      >
        <Controller
          control={control}
          name="neighborhood"
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("Area")}
              value={value}
              onChange={onChange}
              placeholder={t("Choose Area")}
              errorMessage={errors.neighborhood?.message}
              options={areaOptions ?? []}
            />
          )}
        />
      </FormField>

      <FormField
        errorMessage={
          errors.sizeSFee?.message ??
          errors.sizeMFee?.message ??
          errors.sizeLFee?.message
        }
      >
        <div className={styles.form_feesField}>
          <Input
            label={t("Size S Fee")}
            placeholder={t("Enter Fee")}
            type="number"
            {...register("sizeSFee")}
          />

          <Input
            label={t("Size M Fee")}
            placeholder={t("Enter Fee")}
            type="number"
            {...register("sizeMFee")}
          />

          <Input
            label={t("Size L Fee")}
            placeholder={t("Enter Fee")}
            type="number"
            {...register("sizeLFee")}
          />
        </div>
      </FormField>

      <Controller
        control={control}
        name="allowCashOnDelivery"
        render={({ field: { value, onChange } }) => (
          <Switch
            label={t("Cash on delivery")}
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
          />
        )}
      />

      <div className={styles.form_separatorLine} />

      <Controller
        control={control}
        name="allowMobileMoney"
        render={({ field: { value, onChange } }) => (
          <Switch
            label={t("Mobile money")}
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
          />
        )}
      />

      <div className={styles.form_separatorLine} />

      <Controller
        control={control}
        name="isExpedition"
        render={({ field: { value, onChange } }) => (
          <Switch
            label={t("This zone is an expedition")}
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
          />
        )}
      />

      <div className={styles.form_separatorLine} />

      <Controller
        control={control}
        name="isHiddenFromResellers"
        render={({ field: { value, onChange } }) => (
          <>
            <Switch
              label={t("Hidden from resellers")}
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
            />
            <p className={styles.form_hint}>
              {t(
                "For internal zones such as office pickup. The zone disappears from the reseller app and from the assistant, and only the users below can still order into it."
              )}
            </p>
          </>
        )}
      />

      {/* The picker only exists while the zone is hidden — an allowlist on a
          visible zone would suggest it restricts something, and it does not. */}
      <If condition={isHiddenFromResellers}>
        <Controller
          control={control}
          name="allowedResellerIds"
          render={({ field: { value, onChange } }) => (
            <FormField label={t("Who can still see it")}>
              <AllowedResellersField
                initialSelected={initialAllowedResellers}
                value={value}
                onChange={onChange}
              />
            </FormField>
          )}
        />
      </If>
    </form>
  );
};

export default DeliveryFeeForm;
